import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, Phone, MessageSquare, Server, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  ok: { label: 'OK', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', icon: CheckCircle2 },
  drift: { label: 'DRIFT', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', icon: AlertTriangle },
  expected_unknown: { label: 'No canonical', color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400', icon: Server },
  no_twilio_sid: { label: 'No Twilio SID', color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400', icon: Server },
  twilio_error: { label: 'Twilio error', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', icon: ShieldAlert },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.expected_unknown;
  const Icon = meta.icon;
  return (
    <Badge className={`gap-1 ${meta.color}`} data-testid={`status-${status}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </Badge>
  );
};

const CodeCell = ({ value, expected }) => {
  if (!value) return <span className="text-muted-foreground italic text-xs">(unset)</span>;
  const matches = expected && value.replace(/\/$/, '') === expected.replace(/\/$/, '');
  return (
    <code className={`text-[11px] break-all px-1 py-0.5 rounded ${matches === false ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' : 'bg-slate-100 dark:bg-slate-800'}`}>
      {value}
    </code>
  );
};

export default function AdminTwilioWebhookStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await adminAPI.getTwilioWebhookStatus();
      setData(data);
    } catch (err) {
      toast.error(`Failed to load: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const driftCount = data?.summary?.drift_total ?? 0;
  const canonical = data?.canonical_urls;
  const canonicalError = data?.canonical_error;

  return (
    <div className="space-y-6" data-testid="twilio-webhook-status">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Twilio Webhook Health</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live diff between actual Twilio webhook URLs and the URLs this environment expects. Read-only.
          </p>
        </div>
        <Button onClick={load} disabled={refreshing} data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Top-line summary */}
      {driftCount > 0 && (
        <Card className="border-red-300 bg-red-50/60 dark:bg-red-900/20 dark:border-red-800" data-testid="drift-banner">
          <CardContent className="py-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800 dark:text-red-300">{driftCount} drift{driftCount === 1 ? '' : 's'} detected</p>
              <p className="text-sm text-red-700 dark:text-red-200/80 mt-0.5">
                Live Twilio webhook URLs do not match this environment&apos;s canonical URLs. Check rows marked DRIFT below.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!canonical && (
        <Card className="border-slate-300 bg-slate-50/60 dark:bg-slate-900/30" data-testid="no-canonical-banner">
          <CardContent className="py-4 flex items-start gap-3">
            <Server className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-slate-800 dark:text-slate-200">Canonical URLs unavailable in this environment</p>
              <p className="text-sm text-slate-700 dark:text-slate-300 mt-0.5">{canonicalError}</p>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                Drift detection only runs from a production-authorized host. From a preview environment, rows show <code>No canonical</code> instead of OK/DRIFT.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Canonical URLs */}
      {canonical && (
        <Card data-testid="canonical-urls-card">
          <CardContent className="py-4 space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Expected URLs (this env)</p>
            <div className="grid sm:grid-cols-2 gap-2 mt-2 text-xs">
              <div><span className="text-muted-foreground">voice_url: </span><code className="bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">{canonical.voice_url}</code></div>
              <div><span className="text-muted-foreground">sms_url: </span><code className="bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">{canonical.sms_url}</code></div>
              <div><span className="text-muted-foreground">voice_status: </span><code className="bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">{canonical.voice_status}</code></div>
              <div><span className="text-muted-foreground">sms_status: </span><code className="bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">{canonical.sms_status}</code></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Numbers */}
      <section data-testid="numbers-section">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
          <Phone className="w-4 h-4" />
          Tenant phone numbers ({data?.summary?.numbers_total ?? 0})
        </h2>
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 border-b">
                  <tr className="text-left">
                    <th className="py-2 px-3 font-medium">Tenant</th>
                    <th className="py-2 px-3 font-medium">Number</th>
                    <th className="py-2 px-3 font-medium">Status</th>
                    <th className="py-2 px-3 font-medium">Live voice_url</th>
                    <th className="py-2 px-3 font-medium">Live sms_url</th>
                    <th className="py-2 px-3 font-medium">Live status_callback</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.numbers || []).map((n, idx) => (
                    <tr key={`${n.tenant_id}-${n.phone_number}-${idx}`} className="border-b last:border-b-0 align-top" data-testid={`number-row-${idx}`}>
                      <td className="py-2 px-3">
                        <div className="font-medium">{n.tenant_name}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">{n.tenant_id?.slice(0, 8)}…</div>
                      </td>
                      <td className="py-2 px-3 font-mono text-xs">{n.phone_number}</td>
                      <td className="py-2 px-3"><StatusBadge status={n.status} /></td>
                      <td className="py-2 px-3 max-w-[260px]"><CodeCell value={n.live_voice_url} expected={canonical?.voice_url} /></td>
                      <td className="py-2 px-3 max-w-[260px]"><CodeCell value={n.live_sms_url} expected={canonical?.sms_url} /></td>
                      <td className="py-2 px-3 max-w-[260px]"><CodeCell value={n.live_status_callback} expected={canonical?.voice_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Messaging Services */}
      <section data-testid="messaging-services-section">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          Messaging Services ({data?.summary?.messaging_services_total ?? 0})
        </h2>
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 border-b">
                  <tr className="text-left">
                    <th className="py-2 px-3 font-medium">Tenant</th>
                    <th className="py-2 px-3 font-medium">MS SID</th>
                    <th className="py-2 px-3 font-medium">Status</th>
                    <th className="py-2 px-3 font-medium">Live inbound_request_url</th>
                    <th className="py-2 px-3 font-medium">Live status_callback</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.messaging_services || []).length === 0 ? (
                    <tr><td colSpan={5} className="py-6 text-center text-sm text-muted-foreground" data-testid="no-ms-row">No tenants have a Messaging Service provisioned yet.</td></tr>
                  ) : (data?.messaging_services || []).map((m, idx) => (
                    <tr key={`${m.tenant_id}-${m.messaging_service_sid}`} className="border-b last:border-b-0 align-top" data-testid={`ms-row-${idx}`}>
                      <td className="py-2 px-3">
                        <div className="font-medium">{m.tenant_name}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">{m.tenant_id?.slice(0, 8)}…</div>
                      </td>
                      <td className="py-2 px-3 font-mono text-xs">{m.messaging_service_sid}</td>
                      <td className="py-2 px-3"><StatusBadge status={m.status} /></td>
                      <td className="py-2 px-3 max-w-[260px]"><CodeCell value={m.live_inbound_url} expected={canonical?.sms_url} /></td>
                      <td className="py-2 px-3 max-w-[260px]"><CodeCell value={m.live_status_callback} expected={canonical?.sms_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
